import {Tr} from "@/i18n/i18n";
import Scene = Phaser.Scene;
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {SquareButton} from "@/scenes.ui/square-button";
import SpriteUtils from "@/utils/sprite-utils";
import Sprite = Phaser.GameObjects.Sprite;
import CU from "@/utils/c-u";
import {PurchaseInfo} from "@/store/shop/types";
import SoundUtils from "@/utils/sound-utils";
import {NewBoosterPanel} from "@/scenes.ui/new-booster-panel";
import Container = Phaser.GameObjects.Container;
import Image = Phaser.GameObjects.Image;
import BaseSound = Phaser.Sound.BaseSound;

export class MultipleIntroPanel extends Phaser.GameObjects.Container {

    protected onAcceptCallback: (() => unknown) | undefined;

    private static circleShift: number = 25;
    private circleInPos!: number;

    private clickSound!: BaseSound;
    private elements: (Phaser.GameObjects.Text | Phaser.GameObjects.Image)[] = [];

    constructor(scene: Scene, canvas: HTMLCanvasElement, title: string, introElements: IntroPage[],
                onAcceptCallback: () => unknown, clickSound: BaseSound) {
        super(scene);
        this.clickSound = clickSound;
        this.x = canvas.width / 2;
        this.y = canvas.height / 2 - 30;

        this.setDepth(2001);

        let background = SpriteUtils.addSprite(this.scene, 0, 0, Keys.ATLAS_UI, Keys.UI_BLACK).setInteractive();
        SpriteUtils.setDispSize(background, canvas!.width * 2, canvas!.height * 2);
        this.add(background);
        this.preInit(scene, canvas);

        let window = SpriteUtils.addImage(this.scene, 0, 0, Keys.UI_WINDOW_MIDDLE_BIG).setOrigin(0.5, 0.5);
        this.add(window);

        let titleEl = TextUtils.addTextByFamily(this.scene, title, 0, -262, '#ffffff', 16,
            'normal', 420, Fonts.ARIAL_MT)
            .setOrigin(0.5, 0.5);

        this.add([window, titleEl]);
        this.onAcceptCallback = onAcceptCallback;

        this.init(introElements);
    }

    private init(introPages: IntroPage[]) {
        let cnt = introPages.length;
        let pos = -(MultipleIntroPanel.circleShift * (cnt - 1)) / 2;
        this.circleInPos = pos;
        let self = this;

        for (let i = 0; i < cnt; i++) {
            let circle = SpriteUtils.addImage(this.scene!, pos, 90, Keys.FRM_RADIO_OUT, undefined, 0.35).setOrigin(0.5, 0.5).setAlpha(0.9);
            this.add(circle);
            pos += MultipleIntroPanel.circleShift;
        }

        let okButton = new SquareButton(this.scene, 0, 180, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, Tr('dalee').toLocaleUpperCase(), false);

        let next = () => {
            let ip = introPages.shift();
            if (!ip) {
                this.onAcceptCallback!();
                this.close();
                return;
            }

            if(ip.actionName) {
                okButton.setText(ip.actionName.toLocaleUpperCase());
            } else {
                okButton.setText(introPages.length == 0?  Tr('OK').toLocaleUpperCase() : Tr('dalee').toLocaleUpperCase());
            }

            this.elements.forEach(e => e.destroy());
            this.elements = [];

            ip!.introElements.forEach(ie => {
                let y = -80;

                if(this.elements.length > 0) {
                    let lastElement = this.elements[this.elements.length-1];
                    y = lastElement instanceof Phaser.GameObjects.Text? lastElement.getBottomLeft().y!/CU.S + 25 : lastElement.y/CU.S + 25;
                }

                if  (ie instanceof IntroText) {
                    let nameText = TextUtils.addTextByFamily(this.scene, ie.text, 0, y,
                        ie.color, 17, 'bold', 430, Fonts.MYRIADPRO_REGULAR);
                    nameText.setOrigin(0.5, 0);
                    this.elements.push(nameText);
                } else if (ie instanceof IntroImage) {
                    let icon = SpriteUtils.addSprite(this.scene, ie.dx, y + ie.dy, ie.atlas, ie.img, ie.scale);
                    icon.setAlpha(ie.alpha);
                    this.elements.push(icon)
                }
            });
            let minY = this.elements[0].getTopLeft().y!/CU.S;
            let maxY = this.elements[this.elements.length-1].getBottomLeft().y!/CU.S;
            let shift = (maxY - minY) / 2;
            this.elements.forEach(e => e.y -= shift * CU.S);

            this.add(this.elements);
        };
        next();
        self.addCircle();

        let okEnabled = true;
        okButton.setCallback(() => {

            if (!okEnabled) {
                return;
            }
            SoundUtils.play(this.clickSound);
            okEnabled = false;

            this.scene.time.delayedCall(150, () => {
                okEnabled = true;
                if (this.scene) {
                    self.addCircle();
                    next();
                }
            });
        });
        this.add(okButton);
    }

    private addCircle() {
        let circleIn = SpriteUtils.addImage(this.scene!, this.circleInPos, 90, Keys.FRM_RADIO_IN, undefined, 0.35)
            .setOrigin(0.5, 0.5).setAlpha(0.9);
        this.circleInPos += MultipleIntroPanel.circleShift;
        this.add(circleIn);
    }

    protected preInit(scene: Scene, canvas: HTMLCanvasElement) {
        let background = this.scene.add.sprite(0, 0, Keys.ATLAS_UI, Keys.UI_BLACK).setInteractive();
        SpriteUtils.setDispSize(background, canvas!.width * 2, canvas!.height * 2);
        this.add(background);
    }

    protected addCustomButton(x: number, y: number, text: string, buttonBackground: string,
                              buttonAtlas: string | undefined, buttonIcon: string,
                              callback?: () => unknown) {
        let okButton = new SquareButton(this.scene, x, y, 0, buttonBackground,
            buttonAtlas, buttonIcon, text.toLocaleUpperCase(), false);

        okButton.setCallback(() => {
            if (callback != null) {
                callback();
            }
        });

        this.add(okButton);
    }

    public open(): void {
        this.scene.add.existing(this);
    }

    public openAnimated(): void {
        this.open();
        this.setAlpha(0);
        SpriteUtils.scale(this, 0.92);

        this.scene!.tweens.add({
            targets: this,
            alpha: 1,
            scale: 1,
            delay: 0,
            duration: 200
        });
    }

    public close(): void {
        this.setVisible(false);
        this.destroy();
    }
}

export class IntroPage{
    introElements!: (IntroText | IntroImage)[];
    actionName!: string;
    public constructor(introElements: (IntroText | IntroImage)[], actionName?: string) {
        this.introElements = introElements;
        this.actionName = actionName || '';
    }
}

export class IntroText {
    text!: string;
    color!: string;
    public constructor(text: string, color: string) {
        this.text = text;
        this.color = color;
    }
}

export class IntroImage {
    atlas!: string;
    img!: string;
    dx!: number;
    dy!: number;
    scale!: number;
    alpha!: number;
    public constructor(atlas: string, img: string, dx: number, dy: number, scale:number = 1,  alpha: number = 1) {
        this.atlas = atlas;
        this.img = img;
        this.dx = dx;
        this.dy = dy;
        this.scale = scale;
        this.alpha = alpha;
    }
}
